import { gql, TypedDocumentNode, useQuery } from "@apollo/client";
import { useMemo } from "react";

import useTypedContext from "hooks/useTypedContext";
import { Stack } from "types/generated";

import { StackFormContext } from "../context";

type GetAttachedStackEntitiesQueryArgs = {
  stackId: string;
};

type GetAttachedStackEntitiesQuery = {
  stack: Pick<Stack, "attachedPolicies" | "attachedContexts" | "roleBindings" | "integrations">;
};

const GET_ATTACHED_STACK_ENTITIES: TypedDocumentNode<
  GetAttachedStackEntitiesQuery,
  GetAttachedStackEntitiesQueryArgs
> = gql`
  query GetAttachedStackEntities($stackId: ID!) {
    stack(id: $stackId) {
      attachedPolicies {
        id
        isAutoattached
      }
      attachedContexts {
        id
        isAutoattached
      }
      roleBindings {
        id
      }
      integrations {
        awsV2 {
          id
        }
        azure {
          id
        }
      }
    }
  }
`;

const useHasEntities = () => {
  const { createdStackId } = useTypedContext(StackFormContext);

  const { data: entitiesData, loading: entitiesLoading } = useQuery(GET_ATTACHED_STACK_ENTITIES, {
    fetchPolicy: "no-cache",
    variables: {
      stackId: createdStackId,
    },
    skip: !createdStackId,
  });

  const attachedManuallyContexts = useMemo(() => {
    return entitiesData?.stack?.attachedContexts.filter((context) => !context.isAutoattached) || [];
  }, [entitiesData?.stack?.attachedContexts]);

  const attachedManuallyPolicies = useMemo(() => {
    return entitiesData?.stack?.attachedPolicies.filter((policy) => !policy.isAutoattached) || [];
  }, [entitiesData?.stack?.attachedPolicies]);

  const attachedIntegration = useMemo(() => {
    const integrations = entitiesData?.stack?.integrations;
    const awsIntegration = integrations?.awsV2.length && integrations?.awsV2[0];
    const azureIntegration = integrations?.azure.length && integrations?.azure[0];

    return awsIntegration || azureIntegration;
  }, [entitiesData?.stack?.integrations]);

  const stackRoleBindings = useMemo(() => {
    return entitiesData?.stack?.roleBindings || [];
  }, [entitiesData?.stack?.roleBindings]);

  return {
    entitiesLoading,
    hasEntities: Boolean(
      attachedManuallyContexts.length ||
        attachedManuallyPolicies.length ||
        attachedIntegration ||
        stackRoleBindings.length
    ),
  };
};

export default useHasEntities;
